import { type FormEvent } from 'react';
import { useTicketDraft } from './useTicketDraft';
import { config } from '../../app/config';
import { Lock, MessageSquare, Send } from 'lucide-react';
import {
  COMMENT_VISIBILITY,
  type CommentVisibility,
  type TicketComment,
} from '@electrodavisan/shared';
import { Badge, Button, SkeletonList, Textarea, useToast } from '../../components/ui';
import { cn } from '../../lib/cn';
import { formatDateTime } from '../../lib/format';
import { ApiError } from '../../services/api';
import { useAddComment } from '../../services/tickets.service';

type ConversationProps = Parameters<typeof ConversationForm>[0];
export function TicketConversation(props: ConversationProps): JSX.Element {
  return (
    <ConversationForm key={`${props.currentUserSub}:${props.ticketId}:${props.scope}`} {...props} />
  );
}

function ConversationForm({
  ticketId,
  comments,
  isLoading,
  canWriteInternal,
  scope,
  currentUserSub,
  disabled,
  disabledReason,
}: {
  ticketId: string;
  comments: TicketComment[] | undefined;
  isLoading: boolean;
  /** Solo el personal de soporte ve y puede escribir notas internas. */
  canWriteInternal: boolean;
  scope: 'user' | 'support';
  currentUserSub: string | undefined;
  disabled?: boolean;
  disabledReason?: string;
}): JSX.Element {
  const toast = useToast();
  const addComment = useAddComment(scope);
  const draft = useTicketDraft(
    `reply-draft:v1:${encodeURIComponent(config.keycloakUrl + config.keycloakRealm)}:${encodeURIComponent(currentUserSub ?? '')}:${ticketId}:${scope}`,
    { body: '', visibility: COMMENT_VISIBILITY.PUBLIC as string },
  );
  const body = draft.values.body;
  const visibility: CommentVisibility =
    draft.values.visibility === COMMENT_VISIBILITY.INTERNAL
      ? COMMENT_VISIBILITY.INTERNAL
      : COMMENT_VISIBILITY.PUBLIC;
  const restrictedDraft = visibility === COMMENT_VISIBILITY.INTERNAL && !canWriteInternal;
  const setBody = (value: string) => draft.setValues({ body: value, visibility });
  const setVisibility = (value: CommentVisibility) => draft.setValues({ body, visibility: value });

  const handleSubmit = (event: FormEvent): void => {
    event.preventDefault();
    const trimmed = body.trim();
    if (!trimmed || !currentUserSub || restrictedDraft) return;
    const submitted = { ...draft.values };

    addComment.mutate(
      { ticketId, body: trimmed, visibility },
      {
        onSuccess: () => {
          draft.confirm(submitted);
          toast.success(
            visibility === COMMENT_VISIBILITY.INTERNAL
              ? 'Nota interna agregada'
              : 'Respuesta enviada',
          );
        },
        onError: (error) => {
          toast.error(
            'No se pudo enviar',
            error instanceof ApiError ? error.message : 'Intentalo nuevamente.',
          );
        },
      },
    );
  };

  return (
    <div className="space-y-5">
      {draft.recoverable && (
        <section role="status">
          <p>Hay una respuesta guardada en este dispositivo.</p>
          <Button onClick={draft.recover}>Recuperar respuesta</Button>
          <Button onClick={draft.discard}>Descartar borrador</Button>
        </section>
      )}
      {restrictedDraft && (
        <p role="alert">
          Tu permiso para enviar notas internas cambió. Este borrador no se enviará como respuesta
          pública.
        </p>
      )}
      {draft.failed && (
        <p role="alert">
          No se pudo conservar el borrador en este dispositivo. No cierres esta página antes de
          enviarlo.
        </p>
      )}
      {isLoading ? (
        <SkeletonList rows={2} />
      ) : !comments || comments.length === 0 ? (
        <p className="text-sm text-text-muted">Todavia no hay respuestas en este ticket.</p>
      ) : (
        <ul className="space-y-4">
          {comments.map((comment) => {
            const internal = comment.visibility === COMMENT_VISIBILITY.INTERNAL;
            const mine = comment.authorSub === currentUserSub;

            return (
              <li
                key={comment.id}
                className={cn(
                  'rounded-card border p-4',
                  internal
                    ? 'border-warning/40 bg-warning-soft'
                    : mine
                      ? 'border-primary/30 bg-primary-soft'
                      : 'border-border bg-surface-secondary',
                )}
              >
                <div className="mb-2 flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium text-text-primary">
                    {comment.authorName ?? comment.authorEmail ?? 'Usuario'}
                  </span>
                  {comment.authorIsStaff ? <Badge tone="primary">Soporte</Badge> : null}
                  {internal ? (
                    <Badge tone="warning">
                      <Lock className="h-3 w-3" aria-hidden />
                      Nota interna
                    </Badge>
                  ) : null}
                  <span className="ml-auto text-[12px] text-text-muted">
                    {formatDateTime(comment.createdAt)}
                  </span>
                </div>
                <p className="whitespace-pre-wrap break-words text-sm leading-relaxed text-text-secondary">
                  {comment.body}
                </p>
              </li>
            );
          })}
        </ul>
      )}

      {disabled ? (
        <p className="rounded-lg border border-border bg-surface-secondary p-3 text-[13px] text-text-muted">
          {disabledReason ?? 'No es posible responder en este ticket.'}
        </p>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-3">
          <Textarea
            label={
              visibility === COMMENT_VISIBILITY.INTERNAL ? 'Nota interna' : 'Escribe una respuesta'
            }
            value={body}
            disabled={addComment.isPending || !currentUserSub}
            onChange={(event) => setBody(event.target.value)}
            rows={4}
            maxLength={10_000}
            placeholder={
              visibility === COMMENT_VISIBILITY.INTERNAL
                ? 'Visible solo para el equipo de soporte y administradores.'
                : 'Tu respuesta sera visible para el usuario y para soporte.'
            }
          />

          <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
            {canWriteInternal ? (
              <div
                className="inline-flex rounded-lg border border-border p-1"
                role="radiogroup"
                aria-label="Visibilidad del comentario"
              >
                <VisibilityOption
                  active={visibility === COMMENT_VISIBILITY.PUBLIC}
                  onClick={() => setVisibility(COMMENT_VISIBILITY.PUBLIC)}
                  icon={<MessageSquare className="h-3.5 w-3.5" aria-hidden />}
                  label="Respuesta publica"
                />
                <VisibilityOption
                  active={visibility === COMMENT_VISIBILITY.INTERNAL}
                  onClick={() => setVisibility(COMMENT_VISIBILITY.INTERNAL)}
                  icon={<Lock className="h-3.5 w-3.5" aria-hidden />}
                  label="Nota interna"
                />
              </div>
            ) : (
              <span />
            )}

            <Button
              type="submit"
              loading={addComment.isPending}
              disabled={!body.trim() || !currentUserSub || restrictedDraft}
              leftIcon={<Send className="h-4 w-4" aria-hidden />}
            >
              Enviar
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}

function VisibilityOption({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: JSX.Element;
  label: string;
}): JSX.Element {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[13px] font-medium transition-colors',
        active
          ? 'bg-primary-soft text-primary-bright'
          : 'text-text-muted hover:bg-surface-hover hover:text-text-primary',
      )}
    >
      {icon}
      {label}
    </button>
  );
}
